import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import LoginHeader from './components/LoginHeader';
import LoginForm from './components/LoginForm';
import TrustIndicators from './components/TrustIndicators';
import ToastNotification from './components/ToastNotification';

const LoginPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ message: '', type: 'error' });
  const navigate = useNavigate();
  const location = useLocation();

  // API base URL
  const API_URL = import.meta.env?.VITE_SERVER_URL || 'http://localhost:4000';

  useEffect(() => {
    // Check if user is already authenticated
    const token = localStorage.getItem('ddf_token');
    if (token) {
      const redirectPath = location?.state?.from || '/news-generation';
      navigate(redirectPath, { replace: true });
    }

    // Show success message from registration if present
    if (location?.state?.message) {
      setToast({ 
        message: location.state.message, 
        type: location.state.type || 'success' 
      });
    }
  }, [navigate, location]);

  const fetchUserData = async () => {
    const res = await fetch(`${API_URL}/api/user/data`, {
      method: 'GET',
      credentials: 'include'
    });
    const data = await res.json();
    if (data?.success) {
      const user = {
        name: data?.userData?.name,
        email: data?.userData?.email,
        isVerified: data?.userData?.isAccountVerified ?? false,
        pro: data?.userData?.pro ?? false,
        planId: data?.userData?.planId ?? null,
        billingCycle: data?.userData?.billingCycle ?? null
      };
      localStorage.setItem('user', JSON.stringify(user));
    }
  };

  const handleLogin = async (formData) => {
    setLoading(true);
    setError(null);

    try {
      // Call backend login
      const res = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', // include httpOnly cookie
        body: JSON.stringify({ email: formData?.email, password: formData?.password })
      });
      const data = await res.json();
      if (!data?.success) {
        throw new Error(data?.message || 'Invalid email or password');
      }

      // Mark session present for client-side guards (server uses cookies)
      localStorage.setItem('ddf_token', 'present');

      // Fetch user data
      await fetchUserData();

      // Show success toast
      setToast({ message: 'Login successful! Redirecting...', type: 'success' });

      // Redirect after short delay
      setTimeout(() => {
        const redirectPath = location?.state?.from || '/news-generation';
        navigate(redirectPath, { replace: true });
      }, 1000);

    } catch (err) {
      setError(err?.message);
      setToast({ message: err?.message, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const closeToast = () => {
    setToast({ message: '', type: 'error' });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <LoginHeader />
        
        <LoginForm 
          onSubmit={handleLogin}
          loading={loading}
          error={error}
          initialEmail={location?.state?.email}
        />
        
        <TrustIndicators />
      </div>
      <ToastNotification
        message={toast?.message}
        type={toast?.type}
        onClose={closeToast}
      />
    </div>
  );
};

export default LoginPage;